import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { t } from 'i18next';

import { DAY } from '../../components/DaysInputControl/constants';
import { IQuestionEntity } from '../../utils/form';

export const INITIAL_ASSETS_NAMES = {
  CURRENT_ASSETS: 'currentAssets',
  NON_CURRENT_ASSETS: 'nonCurrentAssets',
} as const;

export const CURRENT_ASSETS_NAMES = {
  CASH_EQUIVALENTS: 'cashEquivalents',
  SHORT_TERM_INVESTMENTS: 'shortTermInvestments',
  ACCOUNTS_RECEIVABLE: 'accountsReceivable',
  CREDIT_SALES_PAYMENT_DURATION: 'creditSalesPaymentDuration',
  PERCENT_CREDIT_SALES: 'percentCreditSales',
  INVENTORY_VALUE: 'inventoryValue',
  INVENTORY_SIZE_AVG_MONTHLY_INCOME: 'inventorySizeAvgMonthlyIncome',
  PREPAID_EXPENSES: 'prepaidExpenses',
  OTHER_CURRENT_ASSETS: 'otherCurrentAssets',
} as const;

export const NON_CURRENT_ASSETS_NAMES = {
  INTANGIBLE_ASSETS: 'intangibleAssets',
  INITIAL_COST_PPE: 'initialCostPPE',
  RIGHT_OF_USE_ASSETS: 'rightOfUseAssets',
  GOODWILL: 'goodwill',
  LONG_TERM_INVESTMENTS: 'longTermInvestments',
  DEFERRED_TAX_ASSETS: 'deferredTaxAssets',
  OTHER_NON_CURRENT_ASSETS: 'otherNonCurrentAssets',
} as const;

export const omitFieldsForTotalAssetsSum = [
  CURRENT_ASSETS_NAMES.CREDIT_SALES_PAYMENT_DURATION,
  CURRENT_ASSETS_NAMES.PERCENT_CREDIT_SALES,
  CURRENT_ASSETS_NAMES.INVENTORY_SIZE_AVG_MONTHLY_INCOME,
];

export const useGetAssetsQuestionsLists = () => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
     const currentAssetsQuestionList: IQuestionEntity[] = [
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.currentAssets.currentAssetsQuestions.cashEquivalents',
    ),
    name: CURRENT_ASSETS_NAMES.CASH_EQUIVALENTS,
    adornmentType: 'currency',
  },
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.currentAssets.currentAssetsQuestions.shortTermInvestments',
    ),
    name: CURRENT_ASSETS_NAMES.SHORT_TERM_INVESTMENTS,
    adornmentType: 'currency',
  },
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.currentAssets.currentAssetsQuestions.accountsReceivable',
    ),
    name: CURRENT_ASSETS_NAMES.ACCOUNTS_RECEIVABLE,
    adornmentType: 'currency',
  },
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.currentAssets.currentAssetsQuestions.creditSalesPaymentDuration',
    ),
    name: CURRENT_ASSETS_NAMES.CREDIT_SALES_PAYMENT_DURATION,
    inputType: DAY,
  },
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.currentAssets.currentAssetsQuestions.percentCreditSales',
    ),
    name: CURRENT_ASSETS_NAMES.PERCENT_CREDIT_SALES,
    adornmentType: 'percentage',
  },
  {
    question: t('financialPlan.openingBalanceSheets.initialAssets.currentAssets.currentAssetsQuestions.inventoryValue'),
    name: CURRENT_ASSETS_NAMES.INVENTORY_VALUE,
    adornmentType: 'currency',
  },
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.currentAssets.currentAssetsQuestions.inventorySizeAvgMonthlyIncome',
    ),
    name: CURRENT_ASSETS_NAMES.INVENTORY_SIZE_AVG_MONTHLY_INCOME,
    adornmentType: 'percentage',
    maxValue: 100,
  },
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.currentAssets.currentAssetsQuestions.prepaidExpenses',
    ),
    name: CURRENT_ASSETS_NAMES.PREPAID_EXPENSES,
    adornmentType: 'currency',
  },
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.currentAssets.currentAssetsQuestions.otherCurrentAssets',
    ),
    name: CURRENT_ASSETS_NAMES.OTHER_CURRENT_ASSETS,
    adornmentType: 'currency',
  },
];

 const nonCurrentAssetsQuestionList: IQuestionEntity[] = [
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.nonCurrentAssets.nonCurrentAssetsQuestions.intangibleAssets',
    ),
    name: NON_CURRENT_ASSETS_NAMES.INTANGIBLE_ASSETS,
    adornmentType: 'currency',
  },
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.nonCurrentAssets.nonCurrentAssetsQuestions.initialCostPPE',
    ),
    name: NON_CURRENT_ASSETS_NAMES.INITIAL_COST_PPE,
    adornmentType: 'currency',
  },
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.nonCurrentAssets.nonCurrentAssetsQuestions.rightOfUseAssets',
    ),
    name: NON_CURRENT_ASSETS_NAMES.RIGHT_OF_USE_ASSETS,
    adornmentType: 'currency',
  },
  {
    question: t('financialPlan.openingBalanceSheets.initialAssets.nonCurrentAssets.nonCurrentAssetsQuestions.goodwill'),
    name: NON_CURRENT_ASSETS_NAMES.GOODWILL,
    adornmentType: 'currency',
  },
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.nonCurrentAssets.nonCurrentAssetsQuestions.longTermInvestments',
    ),
    name: NON_CURRENT_ASSETS_NAMES.LONG_TERM_INVESTMENTS,
    adornmentType: 'currency',
  },
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.nonCurrentAssets.nonCurrentAssetsQuestions.deferredTaxAssets',
    ),
    name: NON_CURRENT_ASSETS_NAMES.DEFERRED_TAX_ASSETS,
    adornmentType: 'currency',
  },
  {
    question: t(
      'financialPlan.openingBalanceSheets.initialAssets.nonCurrentAssets.nonCurrentAssetsQuestions.otherNonCurrentAssets',
    ),
    name: NON_CURRENT_ASSETS_NAMES.OTHER_NON_CURRENT_ASSETS,
    adornmentType: 'currency',
  },
];
    return {  currentAssetsQuestionList, nonCurrentAssetsQuestionList};
  }, [t, i18n]);
};

export interface IInitialAssetsValuesType {
  [INITIAL_ASSETS_NAMES.CURRENT_ASSETS]: {
    [CURRENT_ASSETS_NAMES.CASH_EQUIVALENTS]: string;
    [CURRENT_ASSETS_NAMES.SHORT_TERM_INVESTMENTS]: string;
    [CURRENT_ASSETS_NAMES.ACCOUNTS_RECEIVABLE]: string;
    [CURRENT_ASSETS_NAMES.CREDIT_SALES_PAYMENT_DURATION]: string;
    [CURRENT_ASSETS_NAMES.PERCENT_CREDIT_SALES]: string;
    [CURRENT_ASSETS_NAMES.INVENTORY_VALUE]: string;
    [CURRENT_ASSETS_NAMES.INVENTORY_SIZE_AVG_MONTHLY_INCOME]: string;
    [CURRENT_ASSETS_NAMES.PREPAID_EXPENSES]: string;
    [CURRENT_ASSETS_NAMES.OTHER_CURRENT_ASSETS]: string;
  };
  [INITIAL_ASSETS_NAMES.NON_CURRENT_ASSETS]: {
    [NON_CURRENT_ASSETS_NAMES.INTANGIBLE_ASSETS]: string;
    [NON_CURRENT_ASSETS_NAMES.INITIAL_COST_PPE]: string;
    [NON_CURRENT_ASSETS_NAMES.RIGHT_OF_USE_ASSETS]: string;
    [NON_CURRENT_ASSETS_NAMES.GOODWILL]: string;
    [NON_CURRENT_ASSETS_NAMES.LONG_TERM_INVESTMENTS]: string;
    [NON_CURRENT_ASSETS_NAMES.DEFERRED_TAX_ASSETS]: string;
    [NON_CURRENT_ASSETS_NAMES.OTHER_NON_CURRENT_ASSETS]: string;
  };
}

export const defaultCurrentAssetsFieldValue = {
  [CURRENT_ASSETS_NAMES.CASH_EQUIVALENTS]: '',
  [CURRENT_ASSETS_NAMES.SHORT_TERM_INVESTMENTS]: '',
  [CURRENT_ASSETS_NAMES.ACCOUNTS_RECEIVABLE]: '',
  [CURRENT_ASSETS_NAMES.CREDIT_SALES_PAYMENT_DURATION]: '30 days',
  [CURRENT_ASSETS_NAMES.PERCENT_CREDIT_SALES]: '',
  [CURRENT_ASSETS_NAMES.INVENTORY_VALUE]: '',
  [CURRENT_ASSETS_NAMES.INVENTORY_SIZE_AVG_MONTHLY_INCOME]: '',
  [CURRENT_ASSETS_NAMES.PREPAID_EXPENSES]: '',
  [CURRENT_ASSETS_NAMES.OTHER_CURRENT_ASSETS]: '',
};

export const defaultNonCurrentAssetsFieldValue = {
  [NON_CURRENT_ASSETS_NAMES.INTANGIBLE_ASSETS]: '',
  [NON_CURRENT_ASSETS_NAMES.INITIAL_COST_PPE]: '',
  [NON_CURRENT_ASSETS_NAMES.RIGHT_OF_USE_ASSETS]: '',
  [NON_CURRENT_ASSETS_NAMES.GOODWILL]: '',
  [NON_CURRENT_ASSETS_NAMES.LONG_TERM_INVESTMENTS]: '',
  [NON_CURRENT_ASSETS_NAMES.DEFERRED_TAX_ASSETS]: '',
  [NON_CURRENT_ASSETS_NAMES.OTHER_NON_CURRENT_ASSETS]: '',
};

export const initialAssetsInitialValues = {
  [INITIAL_ASSETS_NAMES.CURRENT_ASSETS]: defaultCurrentAssetsFieldValue,
  [INITIAL_ASSETS_NAMES.NON_CURRENT_ASSETS]: defaultNonCurrentAssetsFieldValue,
};
